import axios from 'axios';
import { Sentry } from '@/lib/sentry';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('wca_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — try refresh
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Capture non-401 API errors in Sentry (skip aborts and auth failures)
    if (
      error.response?.status !== 401 &&
      !axios.isCancel(error) &&
      error.response?.status >= 500
    ) {
      Sentry.withScope((scope) => {
        scope.setTag('endpoint', `${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`);
        scope.setExtra('response_status', error.response?.status);
        scope.setExtra('response_data', error.response?.data);
        Sentry.captureException(error);
      });
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('wca_refresh_token');
    if (!refreshToken) {
      localStorage.removeItem('wca_access_token');
      localStorage.removeItem('wca_refresh_token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      const res = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000/v1'}/auth/refresh`,
        { refreshToken },
      );
      const { accessToken, refreshToken: newRefresh } = res.data.data;
      localStorage.setItem('wca_access_token', accessToken);
      localStorage.setItem('wca_refresh_token', newRefresh);
      processQueue(null, accessToken);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('wca_access_token');
      localStorage.removeItem('wca_refresh_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message ?? error.response?.data?.message ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
